import classNames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React from 'react';
import requireIf from 'react-required-if';

import { DISPLAY_DATE_FORMAT, DISPLAY_TIME_FORMAT } from '../../../config/helpers';
import { detailsModalPropTypes, imagePropTypes, iconPropTypes } from '../../../propTypes';
import formatPrice from '../../../util/formatPrice';

import { DEFAULT_CRITICAL_BACKUP_CURRENCY } from '../../../util/currency';
import Icon from '../Atoms/Icon';
import Rating from '../Atoms/Rating';
import FullDetails from '../BookingUpgrades/FullDetails';
import Carousel from '../Carousel';

import formatDateAndTime from '../../../util/formatDateAndTime';
import UpgradeDropdown from '../BookingUpgrades/UpgradeDropdown';

const propTypes = exact({
  arrivalDate: PropTypes.string,
  arrivalText: PropTypes.string,
  arrivalTime: PropTypes.string,
  blockName: PropTypes.string,
  closeModalText: PropTypes.string,
  currencySymbol: PropTypes.string,
  date: PropTypes.string,
  dateText: PropTypes.string,
  departureAirport: PropTypes.string,
  departureAirportText: PropTypes.string,
  departureDate: PropTypes.string,
  departureText: PropTypes.string,
  departureTime: PropTypes.string,
  description: PropTypes.string,
  dropdownLabel: PropTypes.string,
  dropdownOptions: PropTypes.arrayOf(
    PropTypes.shape({
      disabled: PropTypes.bool,
      text: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
  forceVerticalLayout: PropTypes.bool,
  fromPriceText: PropTypes.string,
  handleClick: PropTypes.func,
  handleRemoveClick: PropTypes.func,
  handleDropdownChange: requireIf(PropTypes.func, (props) => Array.isArray(props.dropdownOptions)),
  icons: PropTypes.arrayOf(iconPropTypes),
  iconsTitle: PropTypes.string,
  images: PropTypes.arrayOf(imagePropTypes),
  inboundArrivalDate: PropTypes.string,
  inboundArrivalText: PropTypes.string,
  inboundArrivalTime: PropTypes.string,
  inboundDepartureDate: PropTypes.string,
  inboundDepartureText: PropTypes.string,
  inboundDepartureTime: PropTypes.string,
  isSelected: PropTypes.bool,
  isFromPrice: PropTypes.bool,
  limitedAvailability: PropTypes.bool,
  limitedAvailabilityRemainingSpaces: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  limitedAvailabilityRemainingText: PropTypes.string,
  limitedAvailabilityText: PropTypes.string,
  modalProps: PropTypes.shape(detailsModalPropTypes),
  outboundArrivalDate: PropTypes.string,
  outboundArrivalText: PropTypes.string,
  outboundArrivalTime: PropTypes.string,
  outboundDepartureDate: PropTypes.string,
  outboundDepartureText: PropTypes.string,
  outboundDepartureTime: PropTypes.string,
  price: PropTypes.number,
  pricePerPerson: PropTypes.bool,
  perPersonText: PropTypes.string,
  rating: PropTypes.number,
  removeFromPackageText: PropTypes.string,
  selectedDropdownKey: requireIf(PropTypes.string, (props) => Array.isArray(props.dropdownOptions)),
  selectedText: PropTypes.string,
  soldOut: PropTypes.bool,
  soldOutText: PropTypes.string,
  strapLine: PropTypes.string,
  time: PropTypes.string,
  timeText: PropTypes.string,
  title: PropTypes.string.isRequired,
  type: PropTypes.string,
  useRemoveButton: PropTypes.bool,
  viewDetailsCtaText: PropTypes.string,
});

const defaultProps = {
  arrivalDate: null,
  arrivalTime: null,
  arrivalText: 'Arrives',
  blockName: null,
  closeModalText: 'Close',
  currencySymbol: DEFAULT_CRITICAL_BACKUP_CURRENCY.symbol,
  dateText: 'Date',
  date: null,
  departureAirportText: 'Departure Airport',
  departureAirport: null,
  departureDate: null,
  departureText: 'Departs',
  departureTime: null,
  description: null,
  dropdownLabel: null,
  dropdownOptions: null,
  forceVerticalLayout: false,
  fromPriceText: 'From',
  handleClick: () => false,
  handleRemoveClick: () => false,
  handleDropdownChange: () => false,
  images: null,
  icons: [],
  iconsTitle: null,
  inboundArrivalDate: null,
  inboundArrivalText: 'Inbound Arrives',
  inboundArrivalTime: null,
  inboundDepartureDate: null,
  inboundDepartureText: 'Inbound Departs',
  inboundDepartureTime: null,
  isSelected: false,
  isFromPrice: false,
  limitedAvailability: false,
  limitedAvailabilityRemainingSpaces: null,
  limitedAvailabilityRemainingText: null,
  limitedAvailabilityText: 'Limited Availability',
  modalProps: null,
  outboundArrivalDate: null,
  outboundArrivalText: 'Outbound Arrives',
  outboundArrivalTime: null,
  outboundDepartureDate: null,
  outboundDepartureText: 'Outbound Departs',
  outboundDepartureTime: null,
  perPersonText: 'pp',
  price: null,
  pricePerPerson: false,
  rating: null,
  removeFromPackageText: 'Remove',
  selectedDropdownKey: null,
  selectedText: 'Added to package',
  strapLine: null,
  soldOut: false,
  soldOutText: null,
  time: null,
  timeText: 'Time',
  type: null,
  useRemoveButton: false,
  viewDetailsCtaText: 'View Details',
};

function UpgradeCard(props) {
  const containerClasses = classNames(props.blockName, 'UpgradeCard', {
    selected: props.isSelected,
    'UpgradeCard-forceVerticalLayout': props.forceVerticalLayout,
    'UpgradeCard-allowsRemoveButton': props.useRemoveButton,
  });

  const price = `${props.currencySymbol}${formatPrice(props.price)}${
    props.pricePerPerson ? props.perPersonText : ''
  }`;

  // Checks if HTML value. If not. formats correctly
  const nonHtmlDescription =
    props.description?.charAt(0) === '<'
      ? null
      : props.description?.split('\n').map((item) => <p key={Math.random()}>{item}</p>);

  const limitedAvailabilityText =
    props.limitedAvailabilityRemainingText ||
    props.limitedAvailabilityText ||
    'Limited Availability';

  return (
    <div className={containerClasses}>
      {/* Optional image carousel */}
      {props.images && (
        <div className="UpgradeCard_images">
          <div className="UpgradeCard_carousel">
            <Carousel items={props.images} sideButtons />
          </div>
        </div>
      )}
      <div className="UpgradeCard_container">
        {props.limitedAvailability && !props.soldOut && (
          <div className="UpgradeCard_limitedAvailability">
            {limitedAvailabilityText
              ? limitedAvailabilityText.replace(
                  '{LimitedAvailabilityRemainingSpaces}',
                  props.limitedAvailabilityRemainingSpaces
                )
              : null}
          </div>
        )}
        {/* Card status and price */}
        {!props.soldOut && (
          <div className="UpgradeCard_nav">
            {props.useRemoveButton && (
              <button
                aria-label="Remove"
                type="button"
                className="UpgradeCard_removeButton"
                onClick={props.handleRemoveClick}
              >
                <Icon id="cross" />
                <span className="UpgradeCard_removeButtonLabel">{props.removeFromPackageText}</span>
              </button>
            )}
            <div className="UpgradeCard_selectedIcon">
              <Icon id="tick" />
            </div>
          </div>
        )}
        <div className="UpgradeCard_body">
          <div className="UpgradeCard_content">
            {/* Selectable button */}
            <button
              aria-label="Select"
              type="button"
              className="UpgradeCard_button"
              onClick={props.handleClick}
              disabled={props.soldOut}
            />
            {/* Core details */}
            {props.strapLine && <div className="UpgradeCard_strapLine">{props.strapLine}</div>}
            <h3 className="UpgradeCard_title">{props.title}</h3>
            {props.rating !== null && !Number.isNaN(props.rating) && (
              <div className="UpgradeCard_rating">
                <Rating rating={props.rating} />
              </div>
            )}
            {props.type && props.rating === null && (
              <div className="UpgradeCard_type">{props.type}</div>
            )}
            {props.departureAirport && (
              <div className="UpgradeCard_airport">
                <b>{props.departureAirportText}:</b>
                {` ${props.departureAirport}`}
              </div>
            )}
            {nonHtmlDescription && (
              <div className="UpgradeCard_description">{nonHtmlDescription}</div>
            )}
            {props.description && !nonHtmlDescription && (
              <div
                className="UpgradeCard_description"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: props.description }}
              />
            )}
            {/* Optional icons */}
            {props.icons && !!props.icons.length && (
              <div className="UpgradeCard_icons">
                {props.iconsTitle && (
                  <div className="UpgradeCard_iconsTitle">{props.iconsTitle}</div>
                )}
                <div className="UpgradeCard_iconList">
                  {props.icons.map((icon) => (
                    <Icon
                      className="UpgradeCard_iconItem"
                      key={icon.value + icon.type}
                      value={icon.value}
                      iconSource={icon.type}
                    />
                  ))}
                </div>
              </div>
            )}
            {props.modalProps && props.modalProps.content && (
              <div className="UpgradeCard_viewDetails">
                <FullDetails
                  {...props.modalProps}
                  currencySymbol={props.currencySymbol}
                  closeModalText={props.closeModalText}
                  viewDetailsCtaText={props.viewDetailsCtaText}
                />
              </div>
            )}
            {/* Departure and arrival datetime */}
            {(props.departureDate || props.departureTime) && (
              <div className="UpgradeCard_dateTime">
                <span className="UpgradeCard_dateTimeLabel">{props.departureText}:</span>
                <time
                  className="UpgradeCard_dateTimeValue"
                  dateTime={formatDateAndTime(props.departureDate, props.departureTime)}
                >
                  {formatDateAndTime(props.departureDate, props.departureTime)}
                </time>
              </div>
            )}
            {(props.arrivalDate || props.arrivalTime) && (
              <div className="UpgradeCard_dateTime">
                <span className="UpgradeCard_dateTimeLabel">{props.arrivalText}:</span>
                <time
                  className="UpgradeCard_dateTimeValue"
                  dateTime={formatDateAndTime(props.arrivalDate, props.arrivalTime)}
                >
                  {formatDateAndTime(props.arrivalDate, props.arrivalTime)}
                </time>
              </div>
            )}
            {/* Inbound and outbound datetime */}
            {props.outboundDepartureDate && (
              <div className="UpgradeCard_dateTime">
                <span className="UpgradeCard_dateTimeLabel">{props.outboundDepartureText}:</span>
                <time
                  className="UpgradeCard_dateTimeValue"
                  dateTime={formatDateAndTime(
                    props.outboundDepartureDate,
                    props.outboundDepartureTime
                  )}
                >
                  {formatDateAndTime(props.outboundDepartureDate, props.outboundDepartureTime)}
                </time>
              </div>
            )}
            {props.outboundArrivalDate && (
              <div className="UpgradeCard_dateTime">
                <span className="UpgradeCard_dateTimeLabel">{props.outboundArrivalText}:</span>
                <time
                  className="UpgradeCard_dateTimeValue"
                  dateTime={formatDateAndTime(props.outboundArrivalDate, props.outboundArrivalTime)}
                >
                  {formatDateAndTime(props.outboundArrivalDate, props.outboundArrivalTime)}
                </time>
              </div>
            )}
            {props.inboundDepartureDate && (
              <div className="UpgradeCard_dateTime">
                <span className="UpgradeCard_dateTimeLabel">{props.inboundDepartureText}:</span>
                <time
                  className="UpgradeCard_dateTimeValue"
                  dateTime={formatDateAndTime(
                    props.inboundDepartureDate,
                    props.inboundDepartureTime
                  )}
                >
                  {formatDateAndTime(props.inboundDepartureDate, props.inboundDepartureTime)}
                </time>
              </div>
            )}
            {props.inboundArrivalDate && (
              <div className="UpgradeCard_dateTime">
                <span className="UpgradeCard_dateTimeLabel">{props.inboundArrivalText}:</span>
                <time
                  className="UpgradeCard_dateTimeValue"
                  dateTime={formatDateAndTime(props.inboundArrivalDate, props.inboundArrivalTime)}
                >
                  {formatDateAndTime(props.inboundArrivalDate, props.inboundArrivalTime)}
                </time>
              </div>
            )}
            {/* Date and Time only rendered separately */}
            {props.date && (
              <div className="UpgradeCard_dateTime">
                <span className="UpgradeCard_dateTimeLabel">{props.dateText}: </span>
                {moment(props.date).format(DISPLAY_DATE_FORMAT)}
              </div>
            )}
            {props.time && (
              <div className="UpgradeCard_dateTime">
                <span className="UpgradeCard_dateTimeLabel">{props.timeText}: </span>
                {/* Only need time, but must supply a date to format correctly */}
                {moment(`1970-01-01T${props.time}`).format(DISPLAY_TIME_FORMAT)}
              </div>
            )}
            {/* Optional dropdown options */}
            {props.dropdownOptions && (
              <UpgradeDropdown
                options={props.dropdownOptions}
                selectedValue={props.selectedDropdownKey}
                handleChange={props.handleDropdownChange}
                hideLabel={!props.dropdownLabel}
                labelText={props.dropdownLabel}
                className="UpgradeCard_dropdownContainer"
              />
            )}
            {!props.soldOut ? (
              <div
                className={`UpgradeCard_status${
                  props.isFromPrice && !props.isSelected ? ' UpgradeCard_status-withFromText' : ''
                }`}
              >
                {props.isSelected && props.selectedText}
                {!props.isSelected && typeof props.price === 'number' && (
                  <>
                    {props.isFromPrice && (
                      <span className="UpgradeCard_fromText">{props.fromPriceText}</span>
                    )}
                    {price}
                  </>
                )}
              </div>
            ) : (
              <div className="UpgradeCard_status">{props.soldOutText}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

UpgradeCard.propTypes = propTypes;
UpgradeCard.defaultProps = defaultProps;

export default UpgradeCard;
